import { select } from "d3";
import { isNumber } from "lodash";
import React, { RefObject, useEffect, useState } from "react";

import { usePlayContext } from "../../pages/Play/play-context";
import { FrameStencils, MarkerPlayer, Point } from "../../types";
import { createNewPlayer, getBallMarkers, getPlayerMarkers } from "../../utils";
import { getSurfacePosition } from "../playing-surface/utils";
import styles from "./fast-place-ghost.module.scss";
import { useGetFastPlacePlayers } from "./use-get-fast-place";

/** ================================
 * Fast Place Ghost
 ================================ */
export type FastPlaceGhostProps = {
  frameStencils: FrameStencils | null;
  courtRef: RefObject<HTMLDivElement>;
  svgRef: RefObject<SVGSVGElement>;
  courtWidthUnits: number;
  courtHeightUnits: number;
  adjustXUnits: number;
  adjustYUnits: number;
};

export const FastPlaceGhost = ({
  frameStencils,
  courtRef,
  svgRef,
  courtWidthUnits,
  courtHeightUnits,
  adjustXUnits,
  adjustYUnits,
}: FastPlaceGhostProps) => {
  const { drawToolbarApi, frameStencilsApi, toolPaletteApi, playStateApi } = usePlayContext();
  const { fastPlace } = drawToolbarApi;
  const { isActive: fastPlaceEnabled, toggle: toggleFastPlace } = fastPlace;
  const { positionLabelType } = toolPaletteApi;
  const { playState } = playStateApi;
  const orientation = playState.orientation;

  const { addStencil } = frameStencilsApi;

  const { playerIdNumbers, getPlayerLabel } = useGetFastPlacePlayers(positionLabelType);

  /** ======== Update Player # ======== */
  /** Update the fast add player number every time a player is added or removed.
   * disable the ghost if all player numbers exist on court */
  const [playerId, setPlayerId] = useState<number>();
  useEffect(() => {
    const playersOnCourt = getPlayerMarkers(frameStencils || []);
    const playerIds = playersOnCourt.map((p) => p.id);
    // get list of all player marker ids that are on the court
    const playerIdsOnCourt = playersOnCourt.map((player) => player.playerIdNumber);

    // get a list of the player ids that are not on the court
    const missingPlayerIds = playerIdNumbers.filter((id) => !playerIdsOnCourt.includes(id));

    const playerHasPossession = getBallMarkers(frameStencils || []).some((ball) =>
      playerIds.includes(ball.associatedPlayer)
    );

    // set the current ghost player to the min missing player id
    positionLabelType.labelType === "o" ? setPlayerId(1) : setPlayerId(Math.min(...missingPlayerIds));
    // disable the ghost element if all players are on court and someone has the ball
    if (fastPlaceEnabled && missingPlayerIds.length === 0 && playerHasPossession && positionLabelType.labelType !== "o")
      toggleFastPlace();
  }, [frameStencils, fastPlaceEnabled, toggleFastPlace, playerIdNumbers, positionLabelType.labelType]);

  /** ======== Render Ghost on Mousemove ======== */
  const [ghostPos, setGhostPos] = useState<Point>({ x: 0, y: 0 });
  useEffect(() => {
    const courtContainerElement = courtRef.current;
    const svgElement = svgRef.current;

    if (fastPlaceEnabled && courtContainerElement && svgElement) {
      const courtContainer = select(courtContainerElement);

      // on mousemove..
      courtContainer.on("mousemove", function (event: MouseEvent) {
        // get the relative court position
        const { offsetX, offsetY } = event;
        const { x, y } = getSurfacePosition({
          event,
          offsetX,
          offsetY,
          surfaceContainerElement: courtContainerElement,
          svgElement,
          adjustXUnits,
          adjustYUnits,
          surfaceWidthUnits: courtWidthUnits,
          surfaceHeightUnits: courtHeightUnits,
          orientation,
        });

        // set ghost position
        setGhostPos({ x, y });
      });
    }

    return () => {
      if (courtContainerElement) {
        const courtContainer = select(courtContainerElement);
        courtContainer.on("mousemove", null);
      }
    };
  }, [fastPlaceEnabled, courtRef, svgRef, courtWidthUnits, courtHeightUnits, adjustXUnits, adjustYUnits, orientation]);

  /** ======== Place Player On Click ======== */
  useEffect(() => {
    const courtContainerElement = courtRef.current;
    const svgElement = svgRef.current;

    if (playerId && courtContainerElement && svgElement) {
      const courtContainer = select(courtContainerElement);
      // on click court container..
      courtContainer.on("click", function (event: MouseEvent) {
        // if ghost enabled..
        if (fastPlaceEnabled && playerId !== Infinity) {
          // get clicked relative position
          const { offsetX, offsetY } = event;
          const { x, y } = getSurfacePosition({
            event,
            offsetX,
            offsetY,
            surfaceContainerElement: courtContainerElement,
            svgElement,
            adjustXUnits,
            adjustYUnits,
            surfaceWidthUnits: courtWidthUnits,
            surfaceHeightUnits: courtHeightUnits,
            orientation,
          });

          // create new player marker
          const newPlayer: MarkerPlayer = createNewPlayer({
            pos: { x, y },
            playerIdNumber: playerId,
            label: getPlayerLabel(playerId),
          });

          // update play drawing state with new player
          addStencil({ stencil: newPlayer });
        } else {
          // if fastPlace is disabled, remove click listener
          select(this).on("click", null);
        }
      });
    }
  }, [
    fastPlaceEnabled,
    addStencil,
    courtRef,
    svgRef,
    courtWidthUnits,
    courtHeightUnits,
    playerId,
    getPlayerLabel,
    adjustXUnits,
    adjustYUnits,
    orientation,
  ]);

  return fastPlaceEnabled ? (
    <g
      className={styles.fastPlaceGhostContainer}
      transform={`translate(${ghostPos.x}, ${ghostPos.y}) rotate(${-orientation})`}
    >
      {isNumber(playerId) && playerId !== Infinity && <text>{getPlayerLabel(playerId)}</text>}
    </g>
  ) : null;
};
